import { useEffect, useRef, useState } from "react";
import { X, AlertTriangle } from "lucide-react";
import { Btn } from "./UI";

export function SlidePanel({ open, onClose, title, subtitle, children, width = "max-w-xl", testId = "slide-panel" }) {
  const bodyRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  useEffect(() => {
    if (open && bodyRef.current) {
      // Reset scroll to top on open so the first form field is always visible
      bodyRef.current.scrollTop = 0;
    }
  }, [open]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70]" data-testid={testId}>
      <div className="absolute inset-0 bg-[#3E2723]/30 backdrop-blur-[2px]" onClick={onClose} />
      <div className={`absolute right-0 top-0 h-full w-full ${width} bg-white shadow-2xl flex flex-col slide-panel-enter`}>
        <div className="flex items-start justify-between gap-3 border-b border-[#F1EEE7] bg-[#FDFCF8] px-6 py-4">
          <div className="min-w-0">
            <h3 className="font-heading text-lg font-bold text-[#3E2723]">{title}</h3>
            {subtitle && <p className="text-xs text-[#7B6157] mt-0.5">{subtitle}</p>}
          </div>
          <button type="button" data-testid="slide-panel-close" aria-label="Close panel" onClick={onClose}
            className="h-8 w-8 shrink-0 rounded-full flex items-center justify-center text-[#7B6157] hover:bg-[#F5F2EB] transition-colors">
            <X size={16} />
          </button>
        </div>
        <div ref={bodyRef} className="flex-1 overflow-y-auto px-6 py-5">{children}</div>
      </div>
    </div>
  );
}

export function ConfirmDialog({ open, onClose, onConfirm, title = "Confirm deletion", description, confirmWord = "DELETE", confirmLabel = "Delete", busy = false, warning = "This action cannot be undone." }) {
  const [text, setText] = useState("");
  useEffect(() => { if (open) setText(""); }, [open]);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open, onClose]);
  if (!open) return null;
  const matches = text.trim().toUpperCase() === confirmWord.toUpperCase();
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#3E2723]/40 backdrop-blur-sm p-4" data-testid="confirm-dialog" onClick={onClose}>
      <div className="w-full max-w-md rounded-2xl bg-white shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 bg-[#FFF5F5] border-b border-[#FFCDD2] px-6 py-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#FFEBEE] text-[#C62828]"><AlertTriangle size={17} /></span>
          <h3 className="font-heading text-base font-bold text-[#3E2723]">{title}</h3>
        </div>
        <div className="px-6 py-5 space-y-4">
          {description && <p className="text-sm text-[#6D4C41]">{description}</p>}
          <p className="text-sm text-[#6D4C41]">{warning} Type <span className="font-bold text-[#C62828] tracking-wide">{confirmWord}</span> to confirm.</p>
          <input data-testid="confirm-input" value={text} onChange={(e) => setText(e.target.value)} placeholder={confirmWord} autoFocus
            className="w-full rounded-lg border border-[#D7CCC8] px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#C62828]/30 focus:border-[#C62828]" />
          <div className="flex justify-end gap-2 pt-1">
            <Btn type="button" variant="ghost" onClick={onClose}>Cancel</Btn>
            <Btn data-testid="confirm-action-btn" variant="danger" disabled={!matches || busy} onClick={onConfirm}>
              {busy ? "Working…" : confirmLabel}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ReadOnlyChip() {
  return <span data-testid="read-only-chip" className="inline-flex items-center gap-1.5 rounded-full bg-[#F5F2EB] px-3 py-1.5 text-xs font-semibold text-[#7B6157]">View only - your role cannot edit this data</span>;
}
